import React, { useEffect, useState } from "react";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Divider,
  Stack,
  Chip,
  IconButton,
  Skeleton,
  Tooltip as MuiTooltip,
  Avatar
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  Timeline,
  FilterAlt,
  DateRange,
  ArrowUpward,
  ArrowDownward,
  Receipt,
  ErrorOutline
} from "@mui/icons-material";

const AverageOrderValue = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("chart");
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 12)),
    end: new Date()
  });
  const [totalSales, setTotalSales] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [averageOrderValue, setAverageOrderValue] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState(0);
  const [prevData, setPrevData] = useState(null);

  const CONFIG = {
    colors: {
      primary: [
        colors.primary[500],
        colors.blueAccent[500],
        colors.greenAccent[500],
        colors.redAccent[500]
      ],
      cardBg: theme.palette.background.default,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary
    }
  };

  const generateSampleData = () => {
    const months = [];
    const now = new Date();
    let totalSales = 0;
    let totalInvoices = 0;

    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const aov = Math.floor(Math.random() * 500) + 100;
      const invoices = Math.floor(Math.random() * 200) + 50;
      const sales = aov * invoices;

      months.push({
        name: `${monthName} '${year}`,
        sales,
        invoices,
        aov: parseFloat(aov.toFixed(2)),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      });

      totalSales += sales;
      totalInvoices += invoices;
    }

    const averageAOV = totalSales / totalInvoices;
    const growth = months.length > 1 ?
      ((months[months.length - 1].aov - months[months.length - 2].aov) / months[months.length - 2].aov) * 100 : 0;

    return {
      data: months,
      totalSales,
      totalInvoices,
      averageAOV: parseFloat(averageAOV.toFixed(2)),
      growth: parseFloat(growth.toFixed(2))
    };
  };

  const calculateStats = (data) => {
    if (data.length === 0) return { totalSales: 0, totalInvoices: 0, averageAOV: 0, growth: 0 };
    const totalSales = data.reduce((sum, item) => sum + item.sales, 0);
    const totalInvoices = data.reduce((sum, item) => sum + item.invoices, 0);
    const averageAOV = totalSales / totalInvoices;
    const growth = data.length > 1 ?
      ((data[data.length - 1].aov - data[data.length - 2].aov) / data[data.length - 2].aov * 100) : 0;
    return {
      totalSales,
      totalInvoices,
      averageAOV: parseFloat(averageAOV.toFixed(2)),
      growth: parseFloat(growth.toFixed(2))
    };
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const handleTimeRangeChange = (range) => {
    const now = new Date();
    let startDate = new Date();
    switch (range) {
      case '3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case '2years':
        startDate.setFullYear(now.getFullYear() - 2);
        break;
      default:
        startDate.setFullYear(now.getFullYear() - 1);
    }
    setDateRange({ start: startDate, end: now });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);
      setPrevData(data);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/averageOrderValue`,
        { cmpid },
        { headers: { Authorization: token } }
      );
      // Check if API response is empty
      if (!response.data || Object.keys(response.data).length === 0) {
        throw new Error("API returned empty data");
      }

      const formattedData = Object.values(response.data).map(item => {
        const dateObj = new Date(item.month + "-01");
        const monthName = dateObj.toLocaleString('default', { month: 'short' });
        const year = dateObj.getFullYear().toString().slice(-2);
        const aov = parseFloat(item.average_amount);
        const invoices = parseInt(item.sales_voucher_count);
        const sales = aov * invoices;
        return {
          name: `${monthName} '${year}`,
          sales,
          invoices,
          aov,
          month: dateObj.getMonth() + 1,
          year: dateObj.getFullYear()
        };
      });

      const stats = calculateStats(formattedData);
      setData(formattedData);
      setTotalSales(stats.totalSales);
      setTotalInvoices(stats.totalInvoices);
      setAverageOrderValue(stats.averageAOV);
      setMonthlyGrowth(stats.growth);

    } catch (err) {
      console.error("Error fetching average order value data:", err);
      setError("No data available. Showing sample data.");
      const sampleData = generateSampleData();
      setData(sampleData.data);
      setTotalSales(sampleData.totalSales);
      setTotalInvoices(sampleData.totalInvoices);
      setAverageOrderValue(sampleData.averageAOV);
      setMonthlyGrowth(sampleData.growth);
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "chart" ? "table" : "chart");
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const exportToCSV = () => {
    const headers = ["Month", "Total Sales", "Invoice Count", "Average Order Value"];
    const rows = data.map(item => [item.name, item.sales, item.invoices, item.aov]);
    let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(row => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "average_order_value.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const salesChange = prevData ? calculateChange(totalSales, calculateStats(prevData).totalSales) : 0;
  const invoicesChange = prevData ? calculateChange(totalInvoices, calculateStats(prevData).totalInvoices) : 0;
  const aovChange = prevData ? calculateChange(averageOrderValue, calculateStats(prevData).averageAOV) : 0;
  const growthChange = prevData ? calculateChange(monthlyGrowth, calculateStats(prevData).growth) : 0;

  return (
    <Card sx={{ 
      height: "100%",
      minHeight: 450,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      transition: "all 0.3s ease",
      '&:hover': {
        boxShadow: theme.shadows[6]
      }
    }}>
      <CardContent sx={{ 
        p: isMobile ? 2 : 3,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              color: CONFIG.colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
              <Avatar
                sx={{
                  bgcolor: colors.primary[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Receipt fontSize="small" />
              </Avatar>
              Average Order Value
              <MuiTooltip title="Total Sales ÷ Number of Invoices (Measure of order value)">
                <InfoOutlined sx={{ 
                  fontSize: 18,
                  color: CONFIG.colors.textSecondary,
                  cursor: "pointer"
                }}/>
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Measure of order value over time
              {/* {dateRange && (
                <span style={{ marginLeft: 8 }}>
                  ({formatDate(dateRange.start)} - {formatDate(dateRange.end)})
                </span>
              )} */}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={viewMode === "chart" ? <TableChart /> : <BarChartIcon />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {viewMode === "chart" ? "Table" : "Chart"}
            </Button>
            
            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        
        {/* Error/Sample Data Notice */}
        {error && (
          <Box>
            <Chip
              label={error}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{ 
                mb: 0.5,
                borderColor: colors.warning,
                color: colors.warning
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box display="grid" gridTemplateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"} gap={2}>
              {/* Total Sales Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Total Sales
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs.{totalSales.toLocaleString()}
                </Typography>
                {salesChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {salesChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: salesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: salesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: salesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(salesChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Total Invoices Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Total Invoices
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {totalInvoices.toLocaleString()}
                </Typography>
                {invoicesChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {invoicesChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: invoicesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: invoicesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: invoicesChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(invoicesChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Average Order Value Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Average Order Value
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5} sx={{
                  color: averageOrderValue > 500 ? colors.greenAccent[500] : 
                         averageOrderValue > 300 ? colors.blueAccent[500] : colors.redAccent[500]
                }}>
                  Rs.{averageOrderValue.toFixed(2)}
                </Typography>
                {aovChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {aovChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: aovChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: aovChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: aovChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(aovChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Monthly Growth Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Monthly Growth
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5} sx={{
                  color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500]
                }}>
                  {monthlyGrowth.toFixed(1)}%
                </Typography>
                {growthChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {monthlyGrowth > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {growthChange.toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table Area */}
        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "chart" ? (
            <Box 
              height="100%" 
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Legend />
                  <Line           
                    type="monotone"
                    dataKey="aov" 
                    name="Average Order Value (Rs.)"
                    stroke={colors.blueAccent[500]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Sales (Rs.)</TableCell>
                    <TableCell align="right">Invoices</TableCell>
                    <TableCell align="right">AOV (Rs.)</TableCell>
                    <TableCell align="right">Growth</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => {
                    const growth = index > 0 ? 
                      ((row.aov - data[index - 1].aov) / data[index - 1].aov * 100) : 0;
                    
                    return (
                      <TableRow key={row.name}>
                        <TableCell component="th" scope="row">
                          {row.name}
                        </TableCell>
                        <TableCell align="right">Rs.{row.sales.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.invoices.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ 
                          color: row.aov > 500 ? colors.greenAccent[500] : 
                                 row.aov > 300 ? colors.blueAccent[500] : colors.redAccent[500],
                          fontWeight: 500
                        }}>
                          Rs.{row.aov.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          color: growth > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                          fontWeight: 500
                        }}>
                          {index > 0 ? `${growth.toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Rs.{data.reduce((sum, item) => sum + item.sales, 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {data.reduce((sum, item) => sum + item.invoices, 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 700,
                      color: averageOrderValue > 500 ? colors.greenAccent[500] : 
                             averageOrderValue > 300 ? colors.blueAccent[500] : colors.redAccent[500]
                    }}>
                      Rs.{averageOrderValue.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 700,
                      color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500]
                    }}>
                      {monthlyGrowth.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AverageOrderValue;